<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { SearchMode } from "~/core/composables/useSearch";
import { Filters } from "~/core/models/filters";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useCancelCampaign } from "~/modules/campaign/composables/useCancelCampaign";
import { useGetCampaign } from "~/modules/campaign/composables/useGetCampaign";
import { PATH_CAMPAIGNS } from "~/modules/campaign/constants";
import { CampaignOMStatus } from "~/modules/campaign/models/CampaignOMStatus.enum";
import { hasOperationModelEnabledInLicense } from "~/modules/campaign/utils/hasOperationModelEnabledInLicense";
import { useFetchDtwinOperationModels } from "~/modules/dtwin/composables/useFetchDtwinOperationModels";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";
import ILxcPlus from "~icons/lxc/plus";
import ILxcClose from "~icons/lxc/x";

const { t } = useI18n();

const route = useRoute();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const { selectedSectorCodes } = storeToRefs(useSectorStore());

const {
  fetchData: fetchCampaign,
  isLoading,
  results: campaign,
  error,
  setFilter,
} = useGetCampaign({
  organizationCode: currentOrganization.value?.code,
  uid: route.params.uid.toString(),
});

const {
  isLoading: isLoadingCancelCampaign,
  error: errorCancelCampaign,
  cancelCampaign,
} = useCancelCampaign({ organizationCode: currentOrganization.value?.code });

const {
  allResults: allOperationModels,
  fetchAllData: fetchAllOperationModels,
  setFilter: setOperationModelsFilter,
} = useFetchDtwinOperationModels({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});

const showCampaignOperationMgrFunnel: Ref<boolean> = ref(false);

const showCancelConfirm: Ref<boolean> = ref(false);

const hasOperationModelLicensed = computed(() =>
  allOperationModels.value
    ? hasOperationModelEnabledInLicense(allOperationModels.value)
    : false,
);

const canUpdateCampaign = computed(
  () =>
    (campaign.value?.status === CampaignOMStatus.SCHEDULED ||
      campaign.value?.status === CampaignOMStatus.RUNNING) &&
    hasOperationModelLicensed.value,
);

async function loadData() {
  setFilter(Filters.SECTORS, selectedSectorCodes.value || []);
  await fetchCampaign();
}

function saved() {
  loadData();
  showCampaignOperationMgrFunnel.value = false;
}

function onClickCancelCampaign() {
  showCancelConfirm.value = true;
}

async function onConfirmCancelCampaign() {
  if (campaign.value?.uid) {
    await cancelCampaign(campaign.value?.uid);

    showCancelConfirm.value = false;

    if (errorCancelCampaign.value) {
      errorCancelCampaign.value.notify(NotificationKey.error);
    } else {
      showNotificationSuccess(t("campaign.details.cancel.success"));
      await loadData();
    }
  }
}

onMounted(async () => {
  await loadData();

  if (campaign.value) {
    setOperationModelsFilter(
      Filters.DTWIN_OPERATION_MODEL_TYPE,
      campaign.value?.operationTypes,
    );
    await fetchAllOperationModels();
  }
});

watch(selectedSectorCodes, loadData);
</script>

<template>
  <page-component
    :is-loading="isLoading"
    :error="error"
    :breadcrumbs="[{ title: campaign?.name }]"
    :back-default-route="PATH_CAMPAIGNS"
    check-license
  >
    <template #top-right>
      <div v-if="campaign && canUpdateCampaign" class="flex gap-4">
        <lxc-button
          :icon="ILxcPlus"
          size="large"
          type="primary"
          :title="t('campaign.details.buttons.edit.label')"
          :disabled="isLoading"
          @click="showCampaignOperationMgrFunnel = true"
        >
          {{ t("campaign.details.buttons.edit.label") }}
        </lxc-button>

        <lxc-button
          v-if="campaign?.cancelable && hasOperationModelLicensed"
          :title="t('campaign.details.buttons.cancel.label')"
          :icon="ILxcClose"
          type="tertiary"
          :text="t('campaign.details.buttons.cancel.label')"
          :disabled="isLoadingCancelCampaign"
          @click="onClickCancelCampaign"
        />
      </div>
    </template>
    <div class="flex gap-[1.875rem]">
      <campaign-operation-mgr-characteristics
        :campaign="campaign"
        class="flex-1"
        @reload-campaign="loadData"
      />
      <campaign-operation-mgr-statistics :campaign="campaign" class="flex-1" />
    </div>
    <div class="mt-8">
      <campaign-operation-mgr-operation-list :campaign="campaign" />
    </div>

    <campaign-operation-mgr-funnel
      v-model:show="showCampaignOperationMgrFunnel"
      :campaign="campaign"
      @saved="saved"
    />

    <confirm-modal
      :is-dialog-visible="showCancelConfirm"
      :title="t('campaign.details.buttons.cancel.confirm.title')"
      :description="t('campaign.details.buttons.cancel.confirm.message')"
      :ok-label="t('button.confirm')"
      :cancel-label="t('button.cancel')"
      :icon="ILxcLightTrash2"
      icon-color-theme="error"
      color-theme="danger"
      :is-loading="isLoadingCancelCampaign"
      @confirm="onConfirmCancelCampaign"
      @cancel="showCancelConfirm = false"
    />
  </page-component>
</template>
